/**
 * Lightweight i18n helpers — used across pages to:
 *   - pick the right localized string from multi-language content,
 *   - read the active language from URL search (?lang=) with localStorage fallback,
 *   - build alternate hreflang links for SEO,
 *   - format dates per locale.
 *
 * No i18next dependency — keeps bundle small and SSR-safe.
 */
import type { LanguageCode } from "./theme-context";
import type { Movie } from "./movies-data";

export const SUPPORTED_LANGS: LanguageCode[] = ["ar", "fr", "en"];
export const DEFAULT_LANG: LanguageCode = "ar";

/** Map our language codes to BCP-47 / OG locales. */
export const OG_LOCALE: Record<LanguageCode, string> = {
  ar: "ar_AR",
  fr: "fr_FR",
  en: "en_US",
};

/** Read `?lang=` from a URL search string; returns null if missing/invalid. */
export function readLangParam(search: string | undefined | null): LanguageCode | null {
  if (!search) return null;
  const m = search.match(/[?&]lang=(ar|fr|en)\b/);
  return m ? (m[1] as LanguageCode) : null;
}

/** Pick a localized title from a Movie-shaped object with graceful fallback. */
export function pickTitle(m: Pick<Movie, "title" | "titleEn" | "titleFr">, lang: LanguageCode): string {
  if (lang === "ar") return m.title;
  if (lang === "fr") return m.titleFr || m.titleEn || m.title;
  return m.titleEn || m.title;
}

export function pickOverview(
  m: Pick<Movie, "overview" | "overviewEn" | "overviewFr">,
  lang: LanguageCode,
): string {
  if (lang === "ar") return m.overview;
  if (lang === "fr") return m.overviewFr || m.overviewEn || m.overview;
  return m.overviewEn || m.overview;
}

export function pickGenres(
  m: Pick<Movie, "genres" | "genresEn" | "genresFr">,
  lang: LanguageCode,
): string[] {
  if (lang === "ar") return m.genres;
  if (lang === "fr") return m.genresFr ?? m.genresEn ?? m.genres;
  return m.genresEn ?? m.genres;
}

/** Localized duration with graceful fallback to the base `duration` string. */
export function pickDuration(
  m: Pick<Movie, "duration" | "durationEn" | "durationFr">,
  lang: LanguageCode,
): string {
  if (lang === "ar") return m.duration ?? "";
  if (lang === "fr") return m.durationFr || m.durationEn || m.duration || "";
  return m.durationEn || m.duration || "";
}

/** Localized season-info string (series). Empty string when not provided. */
export function pickSeasonInfo(
  m: Pick<Movie, "seasonInfo" | "seasonInfoEn" | "seasonInfoFr">,
  lang: LanguageCode,
): string {
  if (lang === "ar") return m.seasonInfo ?? "";
  if (lang === "fr") return m.seasonInfoFr || m.seasonInfoEn || m.seasonInfo || "";
  return m.seasonInfoEn || m.seasonInfo || "";
}

/** Build an absolute-looking path with the lang query param appended. */

/** Localized series status with graceful fallback to `status`. */
export function pickStatus(
  m: Pick<Movie, "status" | "statusEn" | "statusFr">,
  lang: LanguageCode,
): string {
  if (lang === "ar") return m.status ?? "";
  if (lang === "fr") return m.statusFr || m.statusEn || m.status || "";
  return m.statusEn || m.status || "";
}

export function withLang(path: string, lang: LanguageCode): string {
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}lang=${lang}`;
}

/** Build the hreflang alternates array for `<link rel="alternate">`. */
export function buildHreflangLinks(basePath: string) {
  return [
    { rel: "alternate", hrefLang: "x-default", href: basePath },
    ...SUPPORTED_LANGS.map((l) => ({
      rel: "alternate",
      hrefLang: l,
      href: withLang(basePath, l),
    })),
  ];
}

/** Format an ISO date string (YYYY-MM-DD) using the active locale; safe fallback. */
export function formatDate(iso: string | undefined, lang: LanguageCode): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // Morocco for Arabic — uses Latin digits and Maghrebi month names (ar-MA).
    const bcp = lang === "ar" ? "ar-MA" : lang === "fr" ? "fr-FR" : "en-US";
    return new Intl.DateTimeFormat(bcp, {
      year: "numeric", month: "long", day: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
}

/** Minimal UI translation table for movie/series pages. */
export const UI_T = {
  ar: {
    movie: "فيلم", series: "مسلسل", trailer: "العرض الترويجي", watch: "مشاهدة",
    download: "تحميل", favorite: "المفضلة", favorited: "في المفضلة",
    save: "حفظ", saved: "محفوظ", share: "مشاركة", shared: "تم النسخ!",
    director: "الإخراج", cast: "البطولة", duration: "المدة", rating: "التقييم",
    releaseDate: "تاريخ الإصدار", firstAir: "أول عرض", lastAir: "آخر عرض",
    status: "الحالة", seasons: "المواسم", episodes: "الحلقات",
    description: "الوصف", related: "قد يعجبك أيضاً",
    notFound: "غير موجود", home: "الرئيسية",
    downloadsTitle: "روابط التحميل والمشاهدة",
    downloadsSubtitle: "اختر الجودة المناسبة لاتصالك",
    selectSeason: "الموسم",
    trending: "رائج", featured: "مميز", newBadge: "جديد",
    views: "مشاهدة", comments: "تعليق",
  },
  fr: {
    movie: "Film", series: "Série", trailer: "Bande-annonce", watch: "Regarder",
    download: "Télécharger", favorite: "Favoris", favorited: "Dans les favoris",
    save: "Enregistrer", saved: "Enregistré", share: "Partager", shared: "Lien copié !",
    director: "Réalisation", cast: "Distribution", duration: "Durée", rating: "Note",
    releaseDate: "Date de sortie", firstAir: "Première diffusion", lastAir: "Dernière diffusion",
    status: "Statut", seasons: "Saisons", episodes: "Épisodes",
    description: "Synopsis", related: "À voir aussi",
    notFound: "Introuvable", home: "Accueil",
    downloadsTitle: "Téléchargements & Streaming",
    downloadsSubtitle: "Choisissez la qualité adaptée à votre connexion",
    selectSeason: "Saison",
    trending: "Tendance", featured: "À la une", newBadge: "Nouveau",
    views: "vues", comments: "commentaires",
  },
  en: {
    movie: "Movie", series: "Series", trailer: "Trailer", watch: "Watch",
    download: "Download", favorite: "Favorite", favorited: "Favorited",
    save: "Save", saved: "Saved", share: "Share", shared: "Copied!",
    director: "Director", cast: "Cast", duration: "Runtime", rating: "Rating",
    releaseDate: "Release date", firstAir: "First aired", lastAir: "Last aired",
    status: "Status", seasons: "Seasons", episodes: "Episodes",
    description: "Synopsis", related: "You may also like",
    notFound: "Not found", home: "Home",
    downloadsTitle: "Download & Watch",
    downloadsSubtitle: "Pick the quality that fits your connection",
    selectSeason: "Season",
    trending: "Trending", featured: "Featured", newBadge: "New",
    views: "views", comments: "comments",
  },
} as const;

export type UITranslations = (typeof UI_T)[LanguageCode];

/** Active language hook for any client component — reads URL first, falls back to ThemeContext. */
export function readActiveLang(searchString: string | undefined, fallback: LanguageCode): LanguageCode {
  return readLangParam(searchString) ?? fallback;
}

/** Tri-lingual string picker. When `fr` is omitted, French falls back to English. */
export function pickT(lang: LanguageCode, ar: string, en: string, fr?: string): string {
  if (lang === "ar") return ar;
  if (lang === "fr") return fr ?? en;
  return en;
}

/** Detect active language for route head/meta functions (URL search first, localStorage on client). */
export function detectHeadLang(search?: Record<string, unknown> | null): LanguageCode {
  const raw = typeof search?.lang === "string" ? search.lang : null;
  if (raw === "ar" || raw === "en" || raw === "fr") return raw;
  if (typeof window !== "undefined") {
    try {
      const saved = window.localStorage.getItem("rawan:language") ?? window.localStorage.getItem("rawan:lang");
      if (saved === "fe") return "fr";
      if (saved === "ar" || saved === "en" || saved === "fr") return saved;
    } catch { /* ignore */ }
  }
  return DEFAULT_LANG;
}

// ---- Compat shims for admin.newsletter & download.$slug.$ep ----
import { useTheme } from "./theme-context";

/** ترجمة سريعة بثلاث لغات: t(lang, ar, en, fr?) */
export function t(lang: LanguageCode, ar: string, en: string, fr?: string): string {
  return pickT(lang, ar, en, fr);
}

/** Hook للحصول على اللغة النشطة + مترجم قصير tt(ar,en,fr) */
export function useI18n() {
  const { language } = useTheme();
  const lang: LanguageCode = (language as LanguageCode) || DEFAULT_LANG;
  return {
    lang,
    language: lang, // alias for callsites that destructure `language`
    ui: UI_T[lang],
    tt: (ar: string, en: string, fr?: string) => pickT(lang, ar, en, fr),
    t: (ar: string, en: string, fr?: string) => pickT(lang, ar, en, fr),
  };
}

